import { createApi } from '@reduxjs/toolkit/query/react'
import { handleError } from '../../commons/utils/utils'
import { setPointsDistributionInfo } from '../slices/pointsDistributionSlice'
import { generateRPCQuery, getBaseQuery } from './apiUtils'
import apiConstants from './constants'

export const pointsDistributionApi = createApi({
  reducerPath: 'pointsDistributionApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: ['PointDistributionDetails'],
  endpoints: (builder) => ({
    getPointsDistribution: builder.query({
      query: (requestParams: GetPointsDistributionParam) =>
        generateRPCQuery(
          apiConstants.POINTS_DISTRIBUTION_RPC_PATH,
          'retrievePointsDistributionList',
          requestParams
        )
    }),
    getPointsDistributionDetails: builder.query({
      query: (requestParams: { PointsDistributionsID: number }) =>
        generateRPCQuery(
          apiConstants.POINTS_DISTRIBUTION_RPC_PATH,
          'getPointsDistributionInfoByID',
          requestParams
        ),
      providesTags: [{ type: 'PointDistributionDetails' }],
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data && data.result) {
            dispatch(setPointsDistributionInfo(data.result))
          }
        } catch (err: any) {
          handleError(err, dispatch)
        }
      }
    }),
    cancelDistribution: builder.mutation({
      query: (requestParams: { PointsDistributionsID: number }) =>
        generateRPCQuery(
          apiConstants.POINTS_DISTRIBUTION_RPC_PATH,
          'cancelPointsDistribution',
          requestParams
        ),
      invalidatesTags: [{ type: 'PointDistributionDetails' }]
    }),
    scheduleCancelledDistribution: builder.mutation({
      query: (requestParams: { PointsDistributionsID: number }) =>
        generateRPCQuery(
          apiConstants.POINTS_DISTRIBUTION_RPC_PATH,
          'scheduleCancelledPointsDistribution',
          requestParams
        ),
      invalidatesTags: [{ type: 'PointDistributionDetails' }]
    }),
    getPointsDistributionUsersRecords: builder.query({
      query: (requestParams: GetPointsDistributionUsersRecordsParam) =>
        generateRPCQuery(
          apiConstants.POINTS_DISTRIBUTION_RPC_PATH,
          'getPointsDistributionUsersRecords',
          requestParams
        )
    }),
    removePointsDistributionUsersRecords: builder.mutation({
      query: (requestParams: RemoveDistributionRecordParams) =>
        generateRPCQuery(
          apiConstants.POINTS_DISTRIBUTION_RPC_PATH,
          'removePointsDistributionUsersRecords',
          requestParams
        ),
      invalidatesTags: [{ type: 'PointDistributionDetails' }]
    }),
    validatePointsDistributionFields: builder.mutation({
      query: (requestParams: BasePointsDistribution) =>
        generateRPCQuery(
          apiConstants.POINTS_DISTRIBUTION_RPC_PATH,
          'validatePointsDistributionFields',
          requestParams
        )
    }),
    createPointsDistribution: builder.mutation({
      query: (requestParams: BasePointsDistribution) =>
        generateRPCQuery(
          apiConstants.POINTS_DISTRIBUTION_RPC_PATH,
          'createNewPointsDistribution',
          requestParams
        )
    }),
    editPointsDistribution: builder.mutation({
      query: (requestParams: BasePointsDistribution) =>
        generateRPCQuery(
          apiConstants.POINTS_DISTRIBUTION_RPC_PATH,
          'editPointsDistribution',
          requestParams
        ),
      invalidatesTags: [{ type: 'PointDistributionDetails' }]
    })
  })
})

export const {
  useCancelDistributionMutation,
  useScheduleCancelledDistributionMutation,
  useRemovePointsDistributionUsersRecordsMutation,
  useGetPointsDistributionDetailsQuery,
  useGetPointsDistributionQuery,
  useGetPointsDistributionUsersRecordsQuery,
  useValidatePointsDistributionFieldsMutation,
  useCreatePointsDistributionMutation,
  useEditPointsDistributionMutation
} = pointsDistributionApi
